import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const { profile } = useAuth();
  const [org,    setOrg]    = useState(null);
  const [limits, setLimits] = useState(null);   // effective limits from org_effective_limits view
  const [usage,  setUsage]  = useState(null);   // current month counts
  const [isSuperAdmin,    setIsSuperAdmin]    = useState(false);
  const [superAdminRole,  setSuperAdminRole]  = useState(null); // 'superadmin' | 'account_manager' | null
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile) {
      setLoading(false);
      return;
    }
    loadTenant();
    checkSuperAdmin();
  }, [profile]);

  const checkSuperAdmin = async () => {
    try {
      const { data } = await supabase
        .from('superadmins')
        .select('id, role')
        .eq('id', profile?.id)
        .maybeSingle();
      setIsSuperAdmin(!!data);
      setSuperAdminRole(data?.role || (data ? 'superadmin' : null));
    } catch {
      setIsSuperAdmin(false);
      setSuperAdminRole(null);
    }
  };

  const loadTenant = async () => {
    if (!profile?.org_id) { setLoading(false); return; }

    try {
      // Load org info + effective limits — view may not exist on some setups
      try {
        const { data: limData } = await supabase
          .from('org_effective_limits')
          .select('*')
          .eq('org_id', profile.org_id)
          .maybeSingle();
        if (limData) setLimits(limData);
      } catch(e) { console.warn('org_effective_limits view missing:', e.message); }

      // Load org details
      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.org_id)
        .maybeSingle();

      if (orgData) setOrg(orgData);

      // Load current month usage
      const month = new Date().toISOString().slice(0, 7);
      const { data: usageData } = await supabase
        .from('usage_counters')
        .select('*')
        .eq('org_id', profile.org_id)
        .eq('month', month)
        .maybeSingle();

      setUsage(usageData || { requests_count: 0, deliveries_count: 0 });
    } catch (err) {
      console.warn('TenantContext loadTenant error:', err.message);
    } finally {
      setLoading(false);          // ← always unblock the spinner
    }
  };

  // ── Limit checker helpers ─────────────────────────────────────
  // Returns { allowed: bool, current: int, max: int, message: string }
  const check = (current, max, label) => {
    if (max === -1) return { allowed: true, current, max, message: '' };
    const allowed = current < max;
    return {
      allowed,
      current,
      max,
      message: allowed
        ? ''
        : `${label} limit reached (${max}). Upgrade your plan to add more.`,
    };
  };

  const canAddAsset     = (count) => check(count,                    limits?.asset_limit,    'Asset');
  const canAddUser      = (count) => check(count,                    limits?.user_limit,     'User');
  const canAddAntenna   = (count) => check(count,                    limits?.antenna_limit,  'Antenna');
  const canAddFacility  = (count) => check(count,                    limits?.facility_limit, 'Facility');
  const canAddRequest   = ()      => check(usage?.requests_count||0, limits?.request_limit,  'Monthly request');
  const canAddDelivery  = ()      => check(usage?.deliveries_count||0,limits?.delivery_limit,'Monthly delivery');

  // ── Usage increment ───────────────────────────────────────────
  const incrementUsage = async (field) => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      const { data: existing } = await supabase
        .from('usage_counters')
        .select('*')
        .eq('org_id', profile.org_id)
        .eq('month', month)
        .maybeSingle();

      const newRow = {
        org_id:           profile.org_id,
        month,
        requests_count:   (existing?.requests_count  || 0) + (field === 'requests'  ? 1 : 0),
        deliveries_count: (existing?.deliveries_count || 0) + (field === 'deliveries' ? 1 : 0),
      };

      const { error } = await supabase
        .from('usage_counters')
        .upsert(newRow, { onConflict: 'org_id,month' });

      if (error) {
        console.error('incrementUsage upsert error:', error.message);
      } else {
        setUsage(newRow);
      }
    } catch(err) {
      console.error('incrementUsage error:', err.message);
    }
  };

  // Feature flags from package — default true if no limits loaded yet (graceful)
  const features = {
    can_checkout:   limits ? (limits.can_checkout   !== false) : true,
    can_requests:   limits ? (limits.can_requests   !== false) : true,
    can_deliveries: limits ? (limits.can_deliveries !== false) : true,
    can_assets:     limits ? (limits.can_assets     !== false) : true,
    can_facilities: limits ? (limits.can_facilities !== false) : true,
    can_reports:    limits ? (limits.can_reports    !== false) : true,
    can_realtime:   limits ? (limits.can_realtime   !== false) : true,
    can_qr_label:   limits ? (limits.can_qr_label   !== false) : true,
    can_floor_maps: limits ? (limits.can_floor_maps !== false) : true,
    can_users:      limits ? (limits.can_users      !== false) : true,
  };

  const value = {
    org, limits, usage, features, isSuperAdmin, superAdminRole, loading,
    canAddAsset, canAddUser, canAddAntenna, canAddFacility,
    canAddRequest, canAddDelivery,
    incrementUsage,
    reload: loadTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>');
  return ctx;
};
